<template>

    <div v-if="can.view" class="flex flex-col w-full xl:w-5/6 max-w-1440 mx-auto">

        <header class="mb-8">
            <div class="flex items-center justify-between rounded-6 bg-white p-24 shadow mb-16">
                <search
                    class="w-2/5"
                    v-model="search"
                    :autofocus="false"
                    :placeholder="$t('jobs.search')"
                />

                <div class="flex items-center gap-12">
                    <feature ref="feature" feature-key="job-sorting" sub-key="jobs">
                        <model-sorting
                            v-if="can.manage"
                            group-id="jobs"
                            :fetch-models="fetchJobsForSorting"
                            @update="updateOrderOfJobs"
                        />
                    </feature>

                    <simple-button v-if="can.create" display="secondary" look="primary" icon="add" @click="startAddJob">
                        {{ $t('jobs.create_job') }}
                    </simple-button>
                </div>
            </div>

            <div class="flex items-center gap-12 flex-wrap mb-8">
                <action-button
                    @click="activeState = activeState === 'active' ? 'archived' : 'active'"
                    no-icon
                >
                    <template #before>
                        <checkbox
                            :checked="activeState === 'archived'"
                            @change="activeState = activeState === 'active' ? 'archived' : 'active'"
                        />
                    </template>
                    <div class="capitalize">{{ $t('jobs.archived_jobs') }}</div>
                </action-button>

                <action-button icon="add" @click="toggleFilter">
                    {{ $t('applications.add_filter') }}
                </action-button>

                <action-button
                    v-if="parametersFetched && location"
                    icon="cancel"
                    @click="location = null"
                >
                    <template #before>
                        {{ $t('settings.locations.location') }}:
                    </template>
                    <span v-if="location === 'remote'">
                        {{ $t('jobs.index_filters.remote') }}
                    </span>
                    <span v-else>
                        {{ locations.find(i => i.value === +location).label }}
                    </span>
                </action-button>

                <action-button
                    v-if="createDateStart || createDateEnd"
                    icon="cancel"
                    @click="createDateStart = null; createDateEnd = null"
                >
                    <template #before>
                        {{ $t('analytics.creation_date') }}:
                    </template>
                    <span v-if="createDateStart">
                        {{ $t('applications.filters.date_from') }} {{ formatDate(createDateStart) }}
                    </span>
                    <span v-if="createDateEnd">
                        {{ $t('applications.filters.date_to') }} {{ formatDate(createDateEnd) }}
                    </span>
                </action-button>

                <action-button
                    v-if="publishDateStart || publishDateEnd"
                    icon="cancel"
                    @click="publishDateStart = null; publishDateEnd = null"
                >
                    <template #before>
                        {{ $t('analytics.publish_date') }}:
                    </template>
                    <span v-if="publishDateStart">
                        {{ $t('applications.filters.date_from') }} {{ formatDate(publishDateStart) }}
                    </span>
                    <span v-if="publishDateEnd">
                        {{ $t('applications.filters.date_to') }} {{ formatDate(publishDateEnd) }}
                    </span>
                </action-button>

                <action-button
                    v-if="archiveDateStart || archiveDateEnd"
                    icon="cancel"
                    @click="archiveDateStart = null; archiveDateEnd = null"
                >
                    <template #before>
                        {{ $t('analytics.archive_date') }}:
                    </template>
                    <span v-if="archiveDateStart">
                        {{ $t('applications.filters.date_from') }} {{ formatDate(archiveDateStart) }}
                    </span>
                    <span v-if="archiveDateEnd">
                        {{ $t('applications.filters.date_to') }} {{ formatDate(archiveDateEnd) }}
                    </span>
                </action-button>

                <action-button
                    v-if="region"
                    icon="cancel"
                    @click="region = null"
                >
                    <template #before>
                        {{ $t('jobs.region') }}:
                    </template>
                    {{ region }}
                </action-button>

                <action-button
                    v-if="parametersFetched && jobReasonAccepted"
                    icon="cancel"
                    @click="jobReasonAccepted = null"
                >
                    <template #before>
                        {{ $t('analytics.info.job_reasons.request.title') }}:
                    </template>
                    {{ getJobReasonTitle(jobReasonAccepted) }}
                </action-button>

                <action-button
                    v-if="parametersFetched && jobReasonRejected"
                    icon="cancel"
                    @click="jobReasonRejected = null"
                >
                    <template #before>
                        {{ $t('analytics.info.job_reasons.reject.title') }}:
                    </template>
                    {{ getJobReasonTitle(jobReasonRejected) }}
                </action-button>

                <action-button
                    v-if="parametersFetched && user"
                    icon="cancel"
                    @click="user = null"
                >
                    <template #before>
                        {{ $t('jobs.filters.responsible_users') }}:
                    </template>
                    {{ users.find(i => i.value === +user).label }}
                </action-button>

                <template v-if="parametersFetched && tags">
                    <template v-for="tag_id in tags">
                        <action-button
                            icon="cancel"
                            @click="removeTag(tag_id)"
                        >
                            <template #before>
                                Tag:
                            </template>
                            {{ allTags.find(i => i.id === +tag_id).title }}
                        </action-button>
                    </template>
                </template>

                <action-button
                    class="!bg-primary-20 hover:!bg-primary-60 !text-primary-80 hover:!text-white"
                    icon="cancel"
                    v-if="showClearFiltersButton"
                    @click="removeAllFilters"
                >
                    {{ $t('applications.filters.clear_filters') }}
                </action-button>
            </div>

            <popup-modal
                v-if="showFilter"
                without-padding
                @close="toggleFilter"
            >
                <div class="flex w-768">
                    <div class="flex-col w-1/3 flex-none border-r border-primary-20 rounded-l overflow-hidden">
                        <ul class="list-reset">
                            <li
                                v-for="(tab, type) in filterTabs"
                                :key="'filter-' + tab.label"
                                :class="getFilterTabClasses(type)"
                                class="flex items-center gap-8 px-16 py-12 cursor-pointer hover:bg-primary hover:text-white hover:border-primary"
                                @click="setFilterTab(type)"
                            >
                                <svg-icon :type="tab.icon" />
                                <span class="first-letter:uppercase">{{ $t(tab.label) }}</span>
                            </li>
                        </ul>
                    </div>
                    <div class="flex flex-col flex-grow pl-32 mr-32 mb-32">
                        <div class="pt-24 mb-32 text-heading-2 font-semibold first-letter:uppercase leading-none text-black">
                            {{ $t('general.filter_on', {subject: $t(filterTabs[filterTabActive]['label'])}) }}
                        </div>
                        <div class="flex-grow mb-32">
                            <div v-if="filterTabActive === 'filter-dates'">
                                <div class="mb-24">
                                    <p class="text-body font-semibold mb-8">{{ $t('analytics.creation_date') }}</p>
                                    <div class="flex flex-col gap-8">
                                        <date-picker :input-label="$t('general.from')" v-model="createDateStart"/>
                                        <date-picker :input-label="$t('general.to')" v-model="createDateEnd"/>
                                    </div>
                                </div>
                                <div class="mb-24">
                                    <p class="text-body font-semibold mb-8">{{ $t('analytics.publish_date') }}</p>
                                    <div class="flex flex-col gap-8">
                                        <date-picker :input-label="$t('general.from')" v-model="publishDateStart"/>
                                        <date-picker :input-label="$t('general.to')" v-model="publishDateEnd"/>
                                    </div>
                                </div>
                                <div class="mb-24">
                                    <p class="text-body font-semibold mb-8">{{ $t('analytics.archive_date') }}</p>
                                    <div class="flex flex-col gap-8">
                                        <date-picker :input-label="$t('general.from')" v-model="archiveDateStart"/>
                                        <date-picker :input-label="$t('general.to')" v-model="archiveDateEnd"/>
                                    </div>
                                </div>
                            </div>
                            <div v-if="filterTabActive === 'filter-locations'" class="flex flex-col gap-8">
                                <select-dropdown
                                    :autoTranslate="false"
                                    icon="location-on"
                                    :input-label="$t('analytics.locations-filter.locations')"
                                    :loading="fetchingParameters"
                                    :limited-items-count="5"
                                    :placeholder="$t('form.dropdown.placeholder').toString()"
                                    :options="locations"
                                    remove-icon
                                    searchable
                                    v-model="location"
                                />

                                <select-dropdown
                                    :auto-translate="false"
                                    class="mr-8 min-w-140"
                                    icon="location-on"
                                    :input-label="$t('analytics.locations-filter.regions')"
                                    :loading="fetchingParameters"
                                    :options="regions"
                                    remove-icon
                                    searchable
                                    v-model="region"
                                />
                            </div>
                            <div v-if="filterTabActive === 'filter-users'">
                                <select-dropdown
                                    :auto-translate="false"
                                    icon="person"
                                    :loading="fetchingParameters"
                                    :options="users"
                                    remove-icon
                                    searchable
                                    v-model="user"
                                />
                            </div>
                            <div v-if="filterTabActive === 'filter-job-reasons'" class="flex flex-col gap-8">
                                <select-dropdown
                                    :auto-translate="false"
                                    can-select-all
                                    :input-label="$t('settings.vacancy.reasons.request')"
                                    label="title"
                                    :loading="fetchingParameters"
                                    :options="jobReasons()"
                                    :placeholder="$t('form.dropdown.placeholder')"
                                    remove-icon
                                    reference="id"
                                    searchable
                                    translatable
                                    v-model="jobReasonAccepted"
                                />

                                <select-dropdown
                                    :auto-translate="false"
                                    can-select-all
                                    :input-label="$t('settings.vacancy.reasons.reject')"
                                    label="title"
                                    :loading="fetchingParameters"
                                    :options="jobReasons(1)"
                                    :placeholder="$t('form.dropdown.placeholder')"
                                    remove-icon
                                    reference="id"
                                    searchable
                                    translatable
                                    v-model="jobReasonRejected"
                                />
                            </div>
                            <div v-if="filterTabActive === 'filter-tags'">
                                <div v-if="allTags.length">
                                    <select-dropdown
                                        :autoTranslate="false"
                                        can-select-all
                                        icon="sell"
                                        label="title"
                                        :loading="fetchingParameters"
                                        :limited-items-count="5"
                                        multiple
                                        :options="allTags"
                                        :placeholder="$t('form.dropdown.placeholder').toString()"
                                        reference="id"
                                        remove-icon
                                        searchable
                                        v-model="tags"
                                    />
                                </div>
                                <template v-else>
                                    <p>{{ $t('jobs.no_linked_tags') }}</p>
                                </template>
                            </div>
                        </div>

                        <simple-button
                            class="ml-auto"
                            display="secondary"
                            icon="replay"
                            look="primary"
                            @click="toggleFilter"
                        >
                            {{ $t('general.apply_filter') }}
                        </simple-button>
                    </div>
                </div>
            </popup-modal>
        </header>

        <div id="job-overview" class="flex-1 flex items-stretch">
            <job-list :filter="search" ref="archivedList"
                      :key="`archived-${renderKeys.archived}`"
                      v-if="showingArchived"
                      active-state="archived"
                      :user-filter="user"
                      :location-filter="location"
                      :tags-filter="tags"
                      :region-filter="region"
                      :create-date-start-filter="createDateStart"
                      :create-date-end-filter="createDateEnd"
                      :publish-date-start-filter="publishDateStart"
                      :publish-date-end-filter="publishDateEnd"
                      :archive-date-start-filter="archiveDateStart"
                      :archive-date-end-filter="archiveDateEnd"
                      :job-reason-accepted-filter="jobReasonAccepted"
                      :job-reason-rejected-filter="jobReasonRejected"
                      @clear="clearAll"/>

            <job-list :filter="search" ref="activeList"
                      :key="`active-${renderKeys.active}`"
                      v-else
                      active-state="active"
                      :user-filter="user"
                      :location-filter="location"
                      :tags-filter="tags"
                      :region-filter="region"
                      :create-date-start-filter="createDateStart"
                      :create-date-end-filter="createDateEnd"
                      :publish-date-start-filter="publishDateStart"
                      :publish-date-end-filter="publishDateEnd"
                      :archive-date-start-filter="archiveDateStart"
                      :archive-date-end-filter="archiveDateEnd"
                      :job-reason-accepted-filter="jobReasonAccepted"
                      :job-reason-rejected-filter="jobReasonRejected"
                      @clear="clearAll"/>
        </div>

        <popup-modal-confirm
            v-if="can.manage && archivingJob"
            :message="$t('jobs.confirm_job_archive', {job: translated(archivingJob.title, locale, true)})"
            :title="$t('jobs.archive_job') + '?'"
            confirm-button="success"
            :confirmText="$t('jobs.archive_job')"
            :working="performingArchiveRequest"
            custom-classes="max-w-640"
            @confirm="archiveJob(archivingJob)"
            @close="cancelArchive"
        >
            <select-dropdown
                v-model="preRejectedReasons"
                reference="id"
                label="title"
                translatable
                :auto-translate="false"
                :multiple="true"
                :loading="fetchingReasons"
                :options="rejectReasons"
                :placeholder="$t('form.dropdown.placeholder')"
                class="my-16"
            >
                {{ $t('settings.vacancy.reasons.reject') }}
            </select-dropdown>

            <template v-if="!fetchingSharedInterimEmails && sharedInterimEmails.length">
                <checkbox
                    v-model="archiveForm.send_mail"
                    class="mr-8 my-16 whitespace-nowrap"
                >
                    <span>{{ $t('jobs.archive.interim.mail.checkbox') }}</span>
                </checkbox>

                <div v-if="archiveForm.send_mail">
                    <p class="text-primary-80 mb-16">
                        {{ $t('jobs.archive.interim.mail.info') }}
                    </p>

                    <simple-input v-model="archiveForm.subject"
                                  class="mb-16">
                        {{ $t('mailer.subject') }}
                    </simple-input>

                    <wysiwyg ref="content"
                             v-model="archiveForm.message"
                             :placeholders="['application', 'company', 'user', 'interviewInvitation']"
                             class="mb-16"
                             with-links
                    >
                        {{ $t('mailer.description') }}

                        <template #after>
                            <div
                                :class="[ signaturePreview ? 'items-start' : 'items-center text-primary-40' ]"
                                class="flex p-16 border-t border-primary-20"
                            >
                                <div class="text-body flex-grow">
                                    <span v-if="signaturePreview" v-html="signaturePreview"></span>
                                    <span v-else>{{ $t('email_signatures.no_signature_selected') }}</span>
                                </div>
                                <select-dropdown
                                    v-model="archiveForm.selectedSignature"
                                    :auto-translate="false"
                                    :options="signatureChoices"
                                    class="min-w-240"
                                />
                            </div>
                        </template>
                    </wysiwyg>
                </div>
            </template>

        </popup-modal-confirm>

        <popup-modal-confirm
            v-if="can.manage && unarchivingJob"
            :message="$t('jobs.confirm_job_unarchive', {job: translated(unarchivingJob.title, locale, true)})"
            :title="$t('jobs.unarchive_job') + '?'"
            confirmButton="success"
            :confirmText="$t('jobs.unarchive_job')"
            :working="performingUnarchiveRequest"
            @confirm="unarchiveJob(unarchivingJob)"
            @close="jobsModuleStore.cancelUnarchivingJob"
        />

        <popup-modal-confirm
            v-if="can.manage && deletingJob"
            :message="$t('jobs.confirm_job_delete', {job: translated(deletingJob.title, locale, true)})"
            :confirmText="$t('jobs.delete_job')"
            :working="performingDeleteRequest"
            @confirm="deleteJob(deletingJob)"
            @close="jobsModuleStore.cancelDeletingJob"
        />
    </div>
    <error-403 v-else></error-403>

</template>

<script>
import Job from '../models/Job';
import Tag from "../models/Tag";
import User from "../models/User";
import Location from "../models/Location";
import JobReason from "../models/JobReason";
import SystemParameter from "../models/SystemParameter";
import {normalizeJson} from '../../shared/helpers';
import {forEach, isArray} from "lodash";
import eventbus from "../../shared/eventbus";
import * as jobReasonService from '../services/JobReasonService';
import * as jobs from '../services/JobService';
import JobList from '../components/JobList.vue';
import SvgIcon from '../components/ui/SvgIcon.vue';
import SimpleButton from '../components/ui/SimpleButton.vue';
import RoundButton from '../components/ui/RoundButton.vue';
import PopupModalConfirm from '../components/ui/PopupModalConfirm.vue';
import Error403 from '../components/ui/Error403.vue';
import Search from '../components/ui/Search.vue';
import SelectDropdown from "../components/forms/SelectDropdown.vue";
import PopupModal from '../components/ui/PopupModal.vue';
import Checkbox from '../components/forms/Checkbox.vue';
import Toggle from "../components/forms/Toggle.vue";
import DatePicker from "../components/forms/DatePicker.vue";
import moment from "moment";
import Feature from '../components/Feature.vue';
import ModelSorting from "../components/ModelSorting.vue";
import {ADD_NOTIFICATION} from "../state/mutation-types";
import SimpleInput from "../components/forms/Input.vue";
import Wysiwyg from "../components/forms/Wysiwyg.vue";
import EmailSignature from "../models/EmailSignature";
import * as signatures from "../services/EmailSignatureService";
import ActionButton from "../components/ui/ActionButton.vue";
import {mapStores} from "pinia";
import {useMainStore} from "../state/store";
import {useJobsStore} from "../state/modules/jobs";
import {useAuthenticationStore} from "../state/modules/authentication";
import {useNotificationsStore} from "../state/modules/notifications";
import {useRepo} from "pinia-orm";

export default {
    components: {
        ActionButton,
        Feature,
        Toggle,
        PopupModal,
        SelectDropdown,
        DatePicker,
        'job-list': JobList,
        'svg-icon': SvgIcon,
        'simple-button': SimpleButton,
        'round-button': RoundButton,
        'popup-modal-confirm': PopupModalConfirm,
        'error-403': Error403,
        'search': Search,
        'checkbox': Checkbox,
        SimpleInput,
        Wysiwyg,
        ModelSorting
    },
    data: function () {
        return {
            renderKeys: {
                'active': 0,
                'archived': 0,
            },
            fetchingSystemParameters: false,
            fetchingParameters: false,
            fetchingReasons: false,
            parametersFetched: false,
            performingArchiveRequest: false,
            performingUnarchiveRequest: false,
            performingDeleteRequest: false,
            inputTimeout: null,
            showFilter: false,
            filterTabActive: '',
            regions: [],
            filter: {
                location: [],
                users: [],
                tags: [],
            },
            filterTabs: {
                'filter-dates': {
                    label: 'general.date.title',
                    icon: 'calendar-today',
                },
                'filter-job-reasons': {
                    label: 'settings.vacancy_reasons.title',
                    icon: 'lightbulb',
                },
                'filter-locations': {
                    label: 'settings.locations.location',
                    icon: 'location-on',
                },
                'filter-users': {
                    label: 'jobs.filters.responsible_users',
                    icon: 'person',
                },
                'filter-tags': {
                    label: 'settings.tags.title',
                    icon: 'sell',
                },
            },
            preRejectedReasons: [],
            archiveForm: null,
        }
    },
    computed: {
        ...mapStores(useMainStore, useJobsStore, useNotificationsStore, useAuthenticationStore),
        dateFormat() {
            return this.mainStore.dateFormat;
        },
        selectedLocale() {
            return this.mainStore.selectedLocale;
        },
        currentCompany() {
            return this.mainStore.currentCompany;
        },
        addingJob() {
            return this.jobsModuleStore.addingJob;
        },
        editingJob() {
            return this.jobsModuleStore.editingJob;
        },
        archivingJob() {
            return this.jobsModuleStore.archivingJob;
        },
        deletingJob() {
            return this.jobsModuleStore.deletingJob;
        },
        unarchivingJob() {
            return this.jobsModuleStore.unarchivingJob;
        },
        fetchingSharedInterimEmails() {
            return this.jobsModuleStore.fetchingSharedInterimEmails;
        },
        sharedInterimEmails() {
            return this.jobsModuleStore.sharedInterimEmails;
        },
        activeState: {
            get() {
                return this.jobsModuleStore.activeState;
            },
            set(newValue) {
                if (newValue === this.activeState) {
                    return;
                }

                let query = {...this.$route.query};
                query.filter = newValue;

                this.$router.replace({query: {...query}});
                this.jobsModuleStore.SET_ACTIVE_STATE(newValue);

                if (newValue === 'archived') {
                    this.clearJobs();
                }
            }
        },
        locale() {
            return this.selectedLocale;
        },
        createDateStart: {
            get() {
                return this.jobsModuleStore.createDateStart;
            },
            set(newValue) {
                this.setFilter(this.dateToString(newValue), 'createDateStart', 'SET_CREATE_DATE_START_FILTER');
            }
        },
        createDateEnd: {
            get() {
                return this.jobsModuleStore.createDateEnd;
            },
            set(newValue) {
                this.setFilter(this.dateToString(newValue), 'createDateEnd', 'SET_CREATE_DATE_END_FILTER');
            }
        },
        publishDateStart: {
            get() {
                return this.jobsModuleStore.publishDateStart;
            },
            set(newValue) {
                this.setFilter(this.dateToString(newValue), 'publishDateStart', 'SET_PUBLISH_DATE_START_FILTER');
            }
        },
        publishDateEnd: {
            get() {
                return this.jobsModuleStore.publishDateEnd;
            },
            set(newValue) {
                this.setFilter(this.dateToString(newValue), 'publishDateEnd', 'SET_PUBLISH_DATE_END_FILTER');
            }
        },
        archiveDateStart: {
            get() {
                return this.jobsModuleStore.archiveDateStart;
            },
            set(newValue) {
                this.setFilter(this.dateToString(newValue), 'archiveDateStart', 'SET_ARCHIVE_DATE_START_FILTER');
            }
        },
        archiveDateEnd: {
            get() {
                return this.jobsModuleStore.archiveDateEnd;
            },
            set(newValue) {
                this.setFilter(this.dateToString(newValue), 'archiveDateEnd', 'SET_ARCHIVE_DATE_END_FILTER');
            }
        },
        unpublished: {
            get() {
                return this.jobsModuleStore.unpublished;
            },
            set(newValue) {
                this.setFilter(newValue, 'unpublished', 'SET_UNPUBLISHED_FILTER');
            }
        },
        jobReasonAccepted: {
            get() {
                return this.jobsModuleStore.jobReasonAccepted;
            },
            set(newValue) {
                this.setFilter(newValue, 'jobReasonAccepted', 'SET_JOB_REASON_ACCEPTED_FILTER');
            }
        },
        jobReasonRejected: {
            get() {
                return this.jobsModuleStore.jobReasonRejected;
            },
            set(newValue) {
                this.setFilter(newValue, 'jobReasonRejected', 'SET_JOB_REASON_REJECTED_FILTER');
            }
        },
        region: {
            get() {
                return this.jobsModuleStore.region;
            },
            set(newValue) {
                this.setFilter(newValue, 'region', 'SET_REGION_FILTER');
            }
        },
        search: {
            get() {
                return this.jobsModuleStore.search;
            },
            set(newValue) {
                if (this.inputTimeout) clearTimeout(this.inputTimeout)
                this.inputTimeout = setTimeout(() => {
                    this.setFilter(newValue, 'search', 'SET_SEARCH_INPUT');
                }, 300)
            }
        },
        location: {
            get() {
                return this.jobsModuleStore.locationFilter;
            },
            set(newValue) {
                this.setFilter(newValue, 'location', 'SET_LOCATION_FILTER');
            }
        },
        user: {
            get() {
                return this.jobsModuleStore.userFilter;
            },
            set(newValue) {
                this.setFilter(newValue, 'user', 'SET_USER_FILTER');
            }
        },
        tags: {
            get() {
                return this.jobsModuleStore.tagsFilter || [];
            },
            set(newValue) {
                this.setFilter(newValue, 'tag_id', 'SET_TAGS_FILTER');
            }
        },
        showingArchived() {
            return this.activeState === 'archived';
        },
        showingActive() {
            return this.activeState === 'active';
        },
        can() {
            return {
                view: this.$can(['jobs.view', 'jobs.view.*', 'jobs.create']),
                create: this.$can(['jobs.manage', 'jobs.create']),
                manage: this.$can(['jobs.manage', 'jobs.manage.*'])
            };
        },
        locations() {
            //Need a better solution for missing alias
            //What if they only filled in one locale alias or don't have a city filled in
            const locations = useRepo(Location).all().map(location => ({
                value: location.id,
                label: typeof location.alias === 'object' && location.alias[this.mainStore.locale] ?
                    location.alias[this.mainStore.locale] :
                    location.city ? location.city : ''
            })).sort((a, b) => a.label.localeCompare(b.label));

            locations.unshift({
                value: 'remote',
                label: this.$t('jobs.index_filters.remote')
            });

            locations.unshift({
                value: null,
                label: this.$t('jobs.index_filters.location')
            });

            return locations;
        },
        users() {
            const users = useRepo(User).all().map(user => ({
                value: user.id,
                label: user.name
            })).sort((a, b) => a.label.localeCompare(b.label));

            users.unshift({
                value: null,
                label: this.$t('jobs.index_filters.users')
            });

            return users;
        },
        allTags() {
            return useRepo(Tag).all().map(tag => ({
                id: tag.id,
                title: tag.title
            })).sort((a, b) => a.title.localeCompare(b.title));
        },
        rejectReasons() {
            return useRepo(JobReason)
                .where('is_rejected', 1)
                .get();
        },
        signatures() {
            return useRepo(EmailSignature).orderBy('created_at').get();
        },
        signaturePreview() {
            return this.signatures.find(signature => signature.id === this.archiveForm.selectedSignature)?.content;
        },
        signatureChoices() {
            return [
                {value: 0, label: this.$t('email_signatures.no_signature_selected')},
                ...this.signatures.map(signature => ({
                    value: signature.id,
                    label: signature.name,
                })),
            ];
        },
        showClearFiltersButton() {
            return this.location
                || this.user
                || this.tags.length
                || this.search
                || this.publishDateStart
                || this.publishDateEnd
                || this.archiveDateStart
                || this.archiveDateEnd
                || this.unpublished
                || this.jobReasonAccepted
                || this.jobReasonRejected
                || this.createDateStart
                || this.createDateEnd
                || this.region;
        },
        languages() {
            return this.currentCompany.jobsite.active_languages;
        },
    },
    methods: {
        // By changing the keys on our components we can force them to rerender.
        // It's a lot easier than trying to retrigger the infinite loader etc.
        clearJobs() {
            this.jobsModuleStore.clear();
            this.renderKeys[this.activeState] += 1;
        },
        clearAll() {
            this.removeAllFilters();
            this.$router.replace({query: {page: '1', filter: ''}}).catch(() => {
            });
        },
        formatDate(date) {
            return moment(date).locale(this.locale).format(this.mainStore.dateFormat);
        },
        dateToString(date) {
            if (moment.isMoment(date)) {
                return date.format('YYYY-MM-DD');
            }
            return date;
        },
        getJobReasonTitle(id) {
            return useRepo(JobReason).find(+id).title[this.locale] || null;
        },
        archiveJob(job) {
            this.performingArchiveRequest = true;

            // attach rejected reasons
            job.reject_reason_ids = this.preRejectedReasons;

            // add archive mail if selected
            if (this.archiveForm.send_mail && this.archiveForm.subject) {
                job.mail = this.archiveForm;
                job.mail.message += this.signaturePreview;
                job.mail.to = this.sharedInterimEmails;
            }

            return jobs.archive(job.id, job)
                .then(result => {
                    eventbus.dispatch('fetch-job-limits')

                    return useRepo(Job).save(normalizeJson(result));
                }).then(inserts => {
                    this.performingArchiveRequest = false;
                    this.preRejectedReasons = [];
                    this.initArchiveForm();
                    this.jobsModuleStore.cancelArchivingJob();
                });
        },
        cancelArchive() {
            this.preRejectedReasons = []
            this.jobsModuleStore.cancelArchivingJob();
        },
        unarchiveJob(job) {
            this.performingUnarchiveRequest = true;

            return jobs.update(job.id, {
                data: {
                    attributes: {
                        archived: null,
                    }
                },
            }).then(result => {
                eventbus.dispatch('fetch-job-limits')

                return useRepo(Job).save(normalizeJson(result));
            }).finally(() => {
                this.performingUnarchiveRequest = false;
                this.jobsModuleStore.cancelUnarchivingJob();
            });
        },
        deleteJob(job) {
            this.performingDeleteRequest = true;

            return jobs.destroy(job.id).then(() => {
                eventbus.dispatch('fetch-job-limits')

                return useRepo(Job).destroy(job.id);
            }).then(() => {
                this.performingDeleteRequest = false;
                this.jobsModuleStore.cancelDeletingJob();
            })
        },
        initFilterAndSearch() {
            this.fetchingParameters = true;

            jobs.filterParameters().then(data => {
                useRepo(Tag).flush();
                const regions = normalizeJson(data.regions);
                if (regions && regions.length) {
                    this.regions = regions.map(region => ({
                        value: region.name,
                        label: region.name
                    }));
                }

                return Promise.all([
                    useRepo(User).save(normalizeJson(data.users)),
                    useRepo(JobReason).save(normalizeJson(data.jobReasons)),
                    useRepo(Location).save(normalizeJson(data.locations)),
                    useRepo(Tag).save(normalizeJson(data.tags)),
                ]);
            }).then(() => {
                this.fetchingParameters = false;
                this.parametersFetched = true;

                // Let's check if the selected location & user filter are valid, if not we'll reset them.
                let query = {...this.$route.query};
                if (this.location && this.location !== 'remote' && !useRepo(Location).find(this.location)) {
                    this.jobsModuleStore.SET_LOCATION_FILTER(null);
                    delete (query.location);
                }
                if (this.user && !useRepo(User).find(this.user)) {
                    this.jobsModuleStore.SET_USER_FILTER(null);
                    delete (query.user);
                }
                if (this.tags && !useRepo(Tag).where('id', this.tags).get().length) {
                    this.jobsModuleStore.SET_TAGS_FILTER(null);
                    delete (query.tag_id);
                }
                if (this.jobReasonAccepted && !useRepo(JobReason).find(this.jobReasonAccepted)) {
                    this.jobsModuleStore.SET_JOB_REASON_ACCEPTED_FILTER(null);
                    delete (query.jobReasonAccepted);
                }
                if (this.jobReasonRejected && !useRepo(JobReason).find(this.jobReasonRejected)) {
                    this.jobsModuleStore.SET_JOB_REASON_REJECTED_FILTER(null);
                    delete (query.jobReasonRejected);
                }

                this.$router.replace({query: {...query}}).catch(() => {});
            });

            let query = {...this.$route.query};

            let filter = query.filter;
            filter = ['active', 'archived'].includes(filter) ? filter : this.jobsModuleStore.activeState;
            this.jobsModuleStore.SET_ACTIVE_STATE(filter);

            query.filter = filter;

            const filters = {
                'search': {key: 'search', command: 'SET_SEARCH_INPUT'},
                'locationFilter': {key: 'location', command: 'SET_LOCATION_FILTER'},
                'userFilter': {key: 'user', command: 'SET_USER_FILTER'},
                'tagsFilter': {key: 'tag_id', command: 'SET_TAGS_FILTER'},

                'createDateStart': {key: 'createDateStart', command: 'SET_CREATE_DATE_START_FILTER'},
                'createDateEnd': {key: 'createDateEnd', command: 'SET_CREATE_DATE_END_FILTER'},

                'publishDateStart': {key: 'publishDate', command: 'SET_PUBLISH_DATE_START_FILTER'},
                'publishDateEnd': {key: 'publishDateEnd', command: 'SET_PUBLISH_DATE_END_FILTER'},

                'archiveDateStart': {key: 'archiveDateStart', command: 'SET_ARCHIVE_DATE_START_FILTER'},
                'archiveDateEnd': {key: 'archiveDateEnd', command: 'SET_ARCHIVE_DATE_END_FILTER'},

                'jobReasonAccepted': {key: 'jobReasonAccepted', command: 'SET_JOB_REASON_ACCEPTED_FILTER'},
                'jobReasonRejected': {key: 'jobReasonRejected', command: 'SET_JOB_REASON_REJECTED_FILTER'},

                'region': {key: 'region', command: 'SET_REGION_FILTER'},
            };

            forEach(filters, (filter, stateKey) => {
                let search = this.jobsModuleStore[stateKey];

                const key = filter.key;
                if (typeof query[key] !== 'undefined' && query[key] && query[key].length > 0) {
                    search = query[key];

                    if (stateKey === 'tagsFilter') {
                        search = !isArray(search) ? [search] : search;
                    }
                } else {
                    query[key] = search;
                }
                this.jobsModuleStore[filter.command](search);
            });

            this.$router.replace({query: {...query}}).catch(() => {});
        },
        startAddJob() {
            this.$router.replace({name: 'jobs.create', query: {from: 'jobs'}});
            this.jobsModuleStore.startAddingJob();
        },
        getFilterTabClasses(tab) {
            if (this.filterTabActive == '') {
                this.filterTabActive = tab;
            }

            let classes = [],
                isLastTab = false,
                keys = Object.keys(this.filterTabs)
            ;

            if (this.filterTabActive == tab) {
                classes.push('bg-primary text-white');
            } else {
                classes.push('text-black');
            }

            for (let i in keys) {
                if ((keys[i] == tab) && (i == (keys.length - 1))) {
                    isLastTab = true;
                    break;
                }
            }

            if (!isLastTab) {
                classes.push('border-b');
                if (this.filterTabActive == tab) {
                    classes.push('border-primary');
                } else {
                    classes.push('border-primary-20');
                }
            }

            return classes;
        },
        setFilterTab(tab) {
            this.filterTabActive = tab;
        },
        toggleFilter() {
            let visible = !this.showFilter;

            // when modal will be visible: always toggle first tab
            if (visible) {
                this.setFilterTab(Object.keys(this.filterTabs)[0]);
            }

            this.showFilter = visible;
        },
        removeAllFilters() {
            this.location = null;
            this.user = null;
            this.tags = [];
            this.search = '';
            this.region = null;
            this.jobReasonAccepted = null;
            this.jobReasonRejected = null;
            this.createDateStart = null;
            this.createDateEnd = null;
            this.publishDateStart = null;
            this.publishDateEnd = null;
            this.archiveDateStart = null;
            this.archiveDateEnd = null;
        },
        removeTag(id) {
            let query = {...this.$route.query};
            let queryTags = query.tag_id || null;

            if (queryTags) {
                queryTags = isArray(queryTags) ? queryTags : [+queryTags];
                forEach(queryTags, (tag, index) => {
                    queryTags[index] = +tag;
                });
            }

            if (queryTags) {
                const index = queryTags.indexOf(+id);
                if (index > -1) {
                    queryTags.splice(index, 1);
                }

                if (!queryTags.length) {
                    delete query.tag_id;
                } else {
                    query.tag_id = queryTags;
                }

                this.$router.replace({query: {...query}});
                this.jobsModuleStore.SET_TAGS_FILTER(queryTags);
                this.clearJobs();
            }
        },
        setFilter(newValue, type, command) {
            if (newValue === this[type]) {
                return;
            }

            let query = {...this.$route.query};
            query[type] = newValue;

            if (!query[type] || query[type].length === 0) {
                delete (query[type]);
            }

            query.page = 1; //reset to page one when searching

            this.$router.replace({query: {...query}});
            this.jobsModuleStore[command](newValue);

            this.clearJobs();
        },
        fetchJobsForSorting() {
            return jobs.index({filters: {active: true}, all: true}).then(result => {
                return normalizeJson(result.data).map(i => ({
                    id: i.id,
                    label: i.title[this.locale],
                    active: i.published && i.active[this.locale]
                })).slice(1);
            });
        },
        updateOrderOfJobs(jobItems) {
            jobs.updateSortSimple(jobItems).then(result => {
                this.refreshJobs();

                this.notificationsModuleStore[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('general.order_changed', {subject: this.$t('general.jobs.title').toLowerCase()}),
                });
            });
        },
        refreshJobs() {
            this.$refs[this.showingActive ? 'activeList' : 'archivedList'].getJobs(1);
        },
        initArchiveForm() {
            this.archiveForm = {
                send_mail: false,
                subject: '',
                message: '',
                to: [],
                selectedSignature: null,
            };
        },
        jobReasons(rejected = 0) {
            // In testing env, this produces errors when the translation isn't found,
            // hence the more verbose code
            let reasons = useRepo(JobReason)
                .where('is_rejected', rejected)
                .get();
            reasons.forEach((reason) => {
                this.formatReasonTranslations(reason)
            })
            reasons.sort((a, b) => a.title[this.locale]?.localeCompare(b.title[this.locale]));
            return reasons;
        },
        formatReasonTranslations(reason) {
            this.languages.forEach((lang) => {
                if (!reason.title[lang]) {
                    reason.title[lang] = null
                }
            })
        },
    },
    watch: {
        'archivingJob'() {
            if (!!this.archivingJob) {
                signatures.index().then(result => {
                    return useRepo(EmailSignature).save(normalizeJson(result.data));
                }).then(() => {
                    this.archiveForm.selectedSignature = this.authenticationStore.defaultSignature?.id || 0;
                });

                this.preRejectedReasons = this.archivingJob.reject_reason_ids
            }
        }
    },
    created() {
        if (!useRepo(SystemParameter).where('key', 'new-applications-influx-threshold').get().length) {
            this.fetchingSystemParameters = true;
            SystemParameter.fetchNewApplicationsInfluxThreshold().then(() => {
                this.fetchingSystemParameters = false;
            });
        }
        if (!useRepo(SystemParameter).where('key', 'new-applications-influx-days').get().length) {
            this.fetchingSystemParameters = true;
            SystemParameter.fetchNewApplicationsInfluxDays().then(() => {
                this.fetchingSystemParameters = false;
            });
        }
        if (!useRepo(SystemParameter).where('key', 'latest-applications-influx').get().length) {
            this.fetchingSystemParameters = true;
            SystemParameter.fetchLatestApplicationsInflux().then(() => {
                this.fetchingSystemParameters = false;
            });
        }
        if (!useRepo(JobReason).all().length) {
            jobReasonService.index().then(({data}) => {
                useRepo(JobReason).save(normalizeJson(data))
            })
        }

        //clear jobs before init infinite loader
        // this.jobsModuleStore.clear();

        this.initFilterAndSearch();
        this.initArchiveForm();
    },
    mounted() {
        this.mainStore.setDocumentTitle(this.$t('jobs.page_title'));
    },
}
</script>
